import axios, { HttpStatusCode } from "axios";

import { NEW_API_URL } from "@repo/core/constants/global";
import useUnicoAuthStore from "@repo/core/stores/unico";
import useAuthStore from "@repo/core/stores/auth";
import { UnicoResource } from "@repo/core/services/resources/unico";
import { logAxiosRequestError } from "@repo/core/services/observability/requestErrorLogger";
import {
  expireAuthenticatedSession,
  rejectStaleSessionResponse,
  startAuthenticatedRequest
} from "@repo/core/services/session/sessionExpiration";
import {
  getSessionEpoch,
  isCurrentSessionEpoch,
  isSessionInvalidated,
  SessionExpiredFlowError
} from "@repo/core/services/session/sessionEpoch";

const newApInstance = axios.create({
  baseURL: NEW_API_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

const unicoResource = new UnicoResource(axios);

let unicoAuthenticationPromise: Promise<string> | null = null;
let unicoAuthenticationEpoch: number | null = null;

const authenticateUnico = async (): Promise<string> => {
  const requestEpoch = getSessionEpoch();

  if (
    !unicoAuthenticationPromise ||
    unicoAuthenticationEpoch !== requestEpoch
  ) {
    const authenticationPromise = unicoResource
      .authenticate()
      .then(({ data }) => data.access_token);

    unicoAuthenticationPromise = authenticationPromise;
    unicoAuthenticationEpoch = requestEpoch;

    void authenticationPromise
      .finally(() => {
        if (unicoAuthenticationPromise !== authenticationPromise) {
          return;
        }

        unicoAuthenticationPromise = null;
        unicoAuthenticationEpoch = null;
      })
      .catch(() => undefined);
  }

  try {
    const token = await unicoAuthenticationPromise;

    if (isSessionInvalidated() || !isCurrentSessionEpoch(requestEpoch)) {
      throw new SessionExpiredFlowError();
    }

    useUnicoAuthStore.getState().update(token);
    return token;
  } catch (error) {
    if (isSessionInvalidated() || !isCurrentSessionEpoch(requestEpoch)) {
      throw new SessionExpiredFlowError();
    }

    throw error;
  }
};

const isUnicoRequest = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  const path = url.split("?")[0];
  return /(^|\/)unico\/(?!auth(?:\/|$))/i.test(path);
};

newApInstance.interceptors.request.use(async (request) => {
  startAuthenticatedRequest(request);

  const unicoToken = useUnicoAuthStore.getState().token;
  const { user } = useAuthStore.getState();
  const userToken = user?.token || process.env.TEST_TOKEN;
  request.headers.Authorization = `Bearer ${userToken}`;

  if (unicoToken) {
    request.headers["unico-token"] = `Bearer ${unicoToken}`;
  } else {
    const token = await authenticateUnico();
    request.headers["unico-token"] = `Bearer ${token}`;
  }

  return request;
});

newApInstance.interceptors.response.use(
  (response) => {
    rejectStaleSessionResponse(response.config);
    return response;
  },
  async (err) => {
    if (err instanceof SessionExpiredFlowError) {
      return Promise.reject(err);
    }

    const { method, url, baseURL } = err?.config || {};
    const { status } = err?.response || {};

    if (__DEV__) {
      console.error(
        `[NEW_API][ERROR][${method?.toUpperCase()}] [${status}] ${JSON.stringify(
          {
            baseURL,
            url,
            data: err?.response?.data,
            request: {
              data: err?.config?.data,
              params: err?.config?.params
            },
            response: err?.response?.data
          }
        )}`
      );
    }

    const shouldReauthenticateUnico =
      err?.response?.status === HttpStatusCode.Unauthorized &&
      isUnicoRequest(err?.config?.url) &&
      !err?.config?._unicoAuthRetried;

    try {
      rejectStaleSessionResponse(err?.config);
    } catch (staleSessionError) {
      return Promise.reject(staleSessionError);
    }

    if (shouldReauthenticateUnico) {
      try {
        err.config._unicoAuthRetried = true;
        const token = await authenticateUnico();
        err.config.headers["unico-token"] = `Bearer ${token}`;

        return await newApInstance.request(err.config);
      } catch (tokenError) {
        if (
          axios.isAxiosError(tokenError) &&
          tokenError.response?.status === HttpStatusCode.Unauthorized
        ) {
          expireAuthenticatedSession({
            apiClient: "new_api",
            error: tokenError
          });
        }
        logAxiosRequestError("new_api", tokenError);
        logAxiosRequestError("new_api", err);
        return Promise.reject(tokenError);
      }
    } else if (err?.response?.status === HttpStatusCode.Unauthorized) {
      expireAuthenticatedSession({ apiClient: "new_api", error: err });
    }

    logAxiosRequestError("new_api", err);

    return Promise.reject(err);
  }
);

export { newApInstance };
